import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { PERMISSIONS_FALSE } from 'src/_mock/permissions';
import { CreateAndUpdateAdmType, PagePermissions, UserPermissions } from 'src/types/users';
import { useAdmsStore } from 'src/store/users/adms.store';
import { paths } from 'src/routes/paths';

export const useAdmDetails = () => {
  const {
    admToEdit,
    isLoadingAdmToEdit,
    isLoadingCreateOrEdit,
    getAdmToEdit,
    createAdm,
    updateAdm,
    resetAdmToEdit,
  } = useAdmsStore();
  const [userPermissions, setUserPermissions] = useState<any>(PERMISSIONS_FALSE);
  const { id } = useParams();
  const { pathname } = useLocation();
  const isEdit = pathname.includes('edit');
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const defaultValues = useMemo(
    () => ({
      id: '',
      name: admToEdit?.name || '',
      email: admToEdit?.email || '',
      password: '',
    }),
    [admToEdit]
  );

  useEffect(() => {
    if (id) {
      getAdmToEdit(id);
    } else {
      resetAdmToEdit();
    }
  }, [id]);

  useEffect(() => {
    const permissions: any = { ...PERMISSIONS_FALSE };
    if (isEdit) {
      if (!admToEdit?.cnAdminInfo?.permissions) {
        setUserPermissions(PERMISSIONS_FALSE);
      } else {
        Object.keys(PERMISSIONS_FALSE).forEach((key) => {
          const permissionKey = key as keyof UserPermissions;

          permissions[permissionKey] = {
            ...PERMISSIONS_FALSE[permissionKey],
            ...(admToEdit?.cnAdminInfo?.permissions[permissionKey] || {}),
          };
        });
        setUserPermissions(permissions);
      }
    } else {
      setUserPermissions(PERMISSIONS_FALSE);
    }
  }, [isEdit, admToEdit]);

  const LoginSchema = Yup.object().shape({
    id: Yup.string(),
    name: Yup.string().required('Nome é obrigatório!'),
    email: Yup.string().email('Digite um e-mail válido').required('E-mail é obrigatório!'),
    password: !isEdit
      ? Yup.string()
        .required('Senha é obrigatória')
        .matches(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&.])[A-Za-z\d@$!%*?&.]{8,}$/,
          'A senha deve conter pelo menos 8 caracteres, uma letra maiúscula, uma letra minúscula, um número e um caractere especial ou ponto.'
        )
      : Yup.string().test(
        'min',
        'A senha deve conter pelo menos 8 caracteres, uma letra maiúscula, uma letra minúscula, um número e um caractere especial ou ponto.',
        (v) => !(v !== '' && !v?.match(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&.])[A-Za-z\d@$!%*?&.]{8,}$/))
      ),
  });

  const onSubmit = async (data: CreateAndUpdateAdmType) => {
    try {
      if (!isEdit) {
        createAdm({ ...data, permissions: userPermissions })
          .then(() => {
            enqueueSnackbar('Usuário cadastrado com sucesso!');
            navigate(paths.dashboard.admsList);
          })
          .catch((e) => {
            enqueueSnackbar(e.message, { variant: 'error' });
          });
      } else {
        updateAdm({ ...data, permissions: userPermissions })
          .then(() => {
            enqueueSnackbar('Usuário editado com sucesso!');
            navigate(paths.dashboard.admsList);
          })
          .catch((e) => {
            enqueueSnackbar(e.message, { variant: 'error' });
          });
      }
    } catch (error) {
      console.log(error);
    }
  };

  const methods = useForm<any>({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const { handleSubmit, reset, watch, setValue } = methods;

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [admToEdit, reset, defaultValues]);

  const onChangePermissions = (e: boolean, tipo: string, auth: string) => {
    if (!userPermissions) {
      return;
    }

    const permissionKey = auth as keyof UserPermissions;
    const currentPermission = {
      ...PERMISSIONS_FALSE[permissionKey],
      ...userPermissions[permissionKey],
    } as PagePermissions & Record<string, boolean>;
    const nextPermission = { ...currentPermission };

    if (tipo === 'read' && !e) {
      Object.keys(nextPermission).forEach((key) => {
        nextPermission[key] = false;
      });
    } else {
      nextPermission[tipo] = e;

      if (e) {
        nextPermission.read = true;
      }
    }

    setUserPermissions({ ...userPermissions, [auth]: nextPermission });
  };

  return {
    isEdit,
    isSubmitting: isLoadingCreateOrEdit,
    methods,
    userPermissions,
    isLoadingAdmToEdit,
    handleSubmit,
    onSubmit,
    reset,
    watch,
    setValue,
    onChangePermissions,
  };
};
